/* eslint-disable camelcase, max-lines */
import React, { useEffect, useState } from 'react'
import Bowser from 'bowser'
import styled from 'styled-components'
import RemoveIcon from '@mui/icons-material/Remove'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

import { StringMap } from 'types/Common'

import ZendeskInput from './ZendeskInput'
import AttachmentUploader from './AttachmentUploader'
import TicketSubmitedMessage from './TicketSubmitedMessage'

import Environment from 'helpers/environment'
import Brand from 'helpers/brand'

import useScreenSize from 'hooks/useScreenSize'
import { useSessionContext } from 'hooks/SessionContext'
import useZendeskTicket from 'hooks/useZendeskTicket'
import { ZendeskTicketPayload } from 'types/Zendesk'
import ZendeskAccounts from './ZendeskAccount'
import { getBrandValue, getDealerTier, mapToRole, subscriptionTypeMap, validateFields } from './ZendeskHelper'

const initilState = {
  name: '',
  email: '',
  auid: '',
  subject: '',
  tokens: []
}

const ZendeskWidget = () => {
  const [open, setOpen] = useState<boolean>(false)
  const { session } = useSessionContext()
  const screenSize = useScreenSize()
  const { sendTicket, loading, success, setSuccess } = useZendeskTicket()
  const [fields, setFields] = useState(initilState)
  const [formErrors, setFormErrors] = useState<StringMap>({})

  const browser = Bowser.getParser(window.navigator.userAgent)
  const browserType = browser.getBrowserName()
  const isProduction = Environment.isProduction()
  const zenDeskAccount = isProduction ? ZendeskAccounts.Production : ZendeskAccounts.Sandbox
  const ZENDESK_CUSTOM_FIELDS = zenDeskAccount.fields

  const updateField = (field: string, value: string | string[]) => {
    setFields((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async () => {
    const errors = validateFields(fields)
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }
    const custom_fields = [
      {
        id: ZENDESK_CUSTOM_FIELDS.AUID,
        value: fields.auid
      },
      {
        id: ZENDESK_CUSTOM_FIELDS.DEALER_TIER,
        value: getDealerTier(session?.dealerPlan?.tier)
      },
      {
        id: ZENDESK_CUSTOM_FIELDS.SUBSCRIPTION_TYPE,
        value: subscriptionTypeMap[session.dealerPlan?.plan]
      },
      {
        id: ZENDESK_CUSTOM_FIELDS.DEALER_GUID,
        value: session.remoteUuid
      },
      {
        id: ZENDESK_CUSTOM_FIELDS.PHONE,
        value: session.dealerPhoneNumber
      },
      {
        id: ZENDESK_CUSTOM_FIELDS.DEALER_CODE,
        value: session.dealerPhoneNumber
      },
      {
        id: ZENDESK_CUSTOM_FIELDS.BROWSER_TYPE,
        value: browserType
      },
      {
        id: ZENDESK_CUSTOM_FIELDS.USER_ID,
        value: session.username
      },
      {
        id: ZENDESK_CUSTOM_FIELDS.ROLE,
        value: mapToRole(session)
      },
      {
        id: ZENDESK_CUSTOM_FIELDS.DEALER_BRAND,
        value: getBrandValue(Brand)
      },
      {
        id: ZENDESK_CUSTOM_FIELDS.DEALER_NAME,
        value: session.dealerName
      }
    ]

    const payload: ZendeskTicketPayload = {
      ticket: {
        'subject': fields.subject,
        comment: {
          'body': fields.subject,
          ...(fields.tokens.length > 0 && {
            uploads: fields.tokens
          })
        },
        custom_fields,
        requester: {
          name: fields.name,
          email: fields.email
        }
      }
    }
    await sendTicket(payload)
  }

  useEffect(() => {
    setFields((prev) => ({
      ...prev,
      name: session?.dealerName || '',
      email: session?.userEmail || ''
    }))
  }, [session])

  return (
    <>
      {!open && (
        <S.FloatingButton onClick={() => setOpen(true)}><HelpOutlineIcon fontSize={screenSize === 'small' ? 'large' : 'medium'} /><span>Support</span></S.FloatingButton>
      )}
      {open && (
        <S.Container>
          <S.Header>
            <S.Title>
              Create a Support Ticket
              <S.CloseButton
                onClick={() => setOpen(false)}
              >
                <RemoveIcon />
              </S.CloseButton>
            </S.Title>
          </S.Header>
          {success
            ? <TicketSubmitedMessage onGoBack={() => {
              setOpen(false)
              setSuccess(false)
              setFields({
                ...initilState,
                name: session?.dealerName || '',
                email: session?.userEmail || ''
              })
            }
            } />
            : (
              <S.FormContainer>
                <S.FormWrapper>
                  <ZendeskInput label={'Your Name'} type="text"
                      id="dealer-name"
                      value={fields.name} onChange={(value: string) => updateField('name', value)} placeholder="Demo IWD Customer"
                      error={formErrors.name}
                  />
                  <ZendeskInput label={'Email Address'} type="email"
                      id="dealer-email"
                      value={fields.email} onChange={(value: string) => updateField('email', value)} placeholder="example@domain.com"
                      error={formErrors.email}
                  />
                  <ZendeskInput label={'AUID (optional)'} type="text"
                      id="device-auid"
                      value={fields.auid} onChange={(value:string) => updateField('auid', value)}
                  />
                  <ZendeskInput label={'How can we help you?'} type="textarea"
                      id="subject"
                      value={fields.subject} onChange={(value:string) => updateField('subject', value)}
                      error={formErrors.subject}
                  />
                  <AttachmentUploader onUploadedChange={(tokens:string[]) => updateField('tokens', tokens)} />
                </S.FormWrapper>
                <S.ButtonWrapper>
                  <Button
                    decoration="positive"
                    text="Send"
                    onPress={handleSubmit}
                    showSpinner={loading}
                  />
                </S.ButtonWrapper>
              </S.FormContainer>
            )}
        </S.Container>
      )}
    </>

  )
}

export default ZendeskWidget

const S = {
  FloatingButton: styled.button`
    position: fixed;
    bottom: 20px;
    right: 20px;
    background:  ${color('primaryButton')};
    color:  ${color('primaryButtonText')};
    border: none;
    border-radius: 2rem;
    padding: 1rem;
    font-size: 1.125rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: bold;
    height: 50px;
    width: 125px;
    @media ${screenSize.small} {
      width: 60px;
      span{
        display: none;
      }
    }
`,
  Header: styled.div`
    background-color: ${color('primaryButton')};
    padding: 1.125rem 1rem;
    position: relative;
    top: 0px;
    width: 100%;
    border-radius: 8px 8px 0px 0px;
    height: 50px;
    @media ${screenSize.small} {
      border-radius: 0px
    }
`,
  CloseButton: styled.button`
    position: absolute;
    right: 10px;
    cursor: pointer;
    font-weight: 800;
    color: ${color('dashboardCardBackground')};
    padding: 0px 6px;
`,
  Container: styled.div`
    width: 100%;
    max-width: 340px;
    position: fixed;
    z-index: 1100;
    bottom: 80px;
    right: 30px;
    margin: 0 auto;
    background:${color('dashboardCardBackground')};
    border-radius: 16px;
    box-shadow: 0 4px 18px ${color('dashboardBackground')};
    height: 60%;
    @media ${screenSize.small} {
      height: 100%;
      bottom: 0;
      right: 0;
      width: 100%;
      max-width: 100%;
    }
  `,
  FormContainer: styled.div`
    height: 100%;
  `,
  FormWrapper: styled.div`
    padding: 1rem 1.25rem 0rem 1.25rem;
    height: calc(100% - 60px);
    overflow: auto;
  `,
  Title: styled.h2`
    color: ${color('dashboardCardBackground')};
    text-align: center;
    font-size: 1.125rem;
    font-weight: 800;
    margin: 0;
    text-transform: none;
  `,
  ButtonWrapper: styled.div`
    display: flex;
    justify-content: flex-end;
    width: 100%;
    background:  ${color('dashboardCardBackground')};
    border-top: 1px solid ${color('border')};
    padding: 0 1.25rem;
    height: 60px;
    border-radius: 0 0 8px 8px;
    @media ${screenSize.small} {
      position: absolute;
      bottom: 0;
    }
  `
}
